import { useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { DayPane } from "./day-pane";
import { DayPaneHeader } from "./day-pane-header";
import { useDayViewScroll } from "./use-day-view-scroll";

/**
 * One day pane wrapped in its own scroll container. Owns the pane's scroll
 * viewport, its "scroll to now" / save-restore behavior, and its current-time
 * anchor — so in the "2 days" split each day scrolls independently (VS Code
 * style) instead of sharing one viewport.
 *
 * Zoom/pan act on a single registered viewport (see calendar.jsx#registerViewport):
 * the primary pane registers on mount so gestures work immediately, and every
 * pane re-registers on pointer-enter so ctrl-wheel zoom and drag-pan target
 * whichever pane the cursor is over. The zoom *level* is global (both panes
 * share `timeAxis`), so rows resize together regardless of which is registered.
 *
 * `isActive` draws a ring around the pane that currently owns zoom/pan (the last
 * one the cursor entered) so it's obvious which day gestures/keys will affect.
 */
export function DayPaneScroller({
  pane,
  showHeader = false,
  paneIndex = 0,
  /** Registers on mount and owns the default viewport for zoom/pan. */
  isPrimary = false,
  /** Highlight this pane as the active (gesture-target) one — split mode only. */
  isActive = false,
  /** Called on pointer-enter so the parent can mark this pane active. */
  onActivate,
  className,
  // Scroll / zoom wiring.
  registerViewport,
  zoomPercent = 100,
  widthZoomPercent = 100,
  /** Cursor anchor for zoom compensation. Pass null in split mode so each pane
   *  keeps its own view centered on zoom (the shared anchor can only be
   *  consumed once, so it can't cursor-anchor two independent panes at once). */
  zoomAnchorRef = null,
  resetSignal = 0,
  initialScrollToNow = true,
  initialScrollRatio = null,
  initialSelectedEventId = null,
  /** Deep-linked appointment to scroll to whenever `focusSignal` bumps. */
  focusEventId = null,
  focusSignal = 0,
  onSaveScrollRatio,
  onDidInitialScroll,
  // DayPane body props.
  use12HourFormat = false,
  timeAxis,
  selectedEventId,
  setSelectedEventId,
  highlightedEventId = null,
  currentTimeRowStart,
  currentTimeRowOffsetPercent,
  timeOffBlocks,
  doctorOptions,
  columnWidthMode = "dynamic",
  columnWidthCustomPx,
  dayViewGroupBy = "tenant",
  dragPlaceholder = null,
  showSessionRail = false,
  railWidthPx = 26,
  railColorMode = "accent",
  railMarginPx = 0,
  /** (columnId, paneDate) → open the session override dialog (doctor grouping only). */
  onSessionAction,
  /** ({ calendarId, dayKey, minute }) → open the "New entry" sheet (standalone-calendar columns). */
  onCreateCalendarEntry,
}) {
  const scrollAreaRef = useRef(null);
  const nowAnchorRef = useRef(null);
  const previousZoomPercentRef = useRef(zoomPercent);

  const splitColumns = Boolean(pane.splitColumns);
  // Horizontal scroll only when this pane's own columns overflow (fixed-width
  // tenant/doctor split) or width-zoom pushes past 100%.
  const scrollOrientation =
    (splitColumns && columnWidthMode !== "dynamic") || widthZoomPercent > 100
      ? "both"
      : "vertical";
  // Reserve a 10px gutter at the bottom for the horizontal scrollbar so it sits
  // under the grid instead of overlaying the last visible row (mirrors pr-2).
  const scrollPaddingClass = scrollOrientation === "both" ? "pr-2 pb-2.5" : "pr-2";

  useDayViewScroll({
    scrollAreaRef,
    nowAnchorRef,
    previousZoomPercentRef,
    initialScrollToNow,
    initialScrollRatio,
    initialSelectedEventId,
    onDidInitialScroll,
    onSaveScrollRatio,
    zoomPercent,
    widthZoomPercent,
    zoomAnchorRef,
    resetSignal,
    focusEventId,
    focusSignal,
  });

  // The primary pane registers its viewport on mount so zoom/pan work before any
  // hover. Re-runs when the mounted ScrollArea node changes (orientation switch
  // renders a different viewport under the same ref).
  useEffect(() => {
    if (typeof registerViewport !== "function" || !isPrimary) {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    registerViewport(viewport || null);
    return () => registerViewport(null);
  }, [registerViewport, isPrimary, scrollOrientation]);

  // Entering the pane (header or body) makes it the active zoom/pan target and
  // marks it active for the highlight.
  const handlePaneEnter = () => {
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (viewport && typeof registerViewport === "function") {
      registerViewport(viewport);
    }
    onActivate?.();
  };

  return (
    <div className={cn("relative flex flex-col", className)} onPointerEnter={handlePaneEnter}>
      {/* Date selector lives OUTSIDE the scroll area — a fixed, centered bar per
          pane so it never scrolls with the timeline (vertically or horizontally). */}
      {showHeader ? (
        <div className="flex-none border-b border-border/60 bg-background">
          <DayPaneHeader
            date={pane.date}
            onDateChange={pane.onDateChange}
            onClose={pane.onClose}
          />
        </div>
      ) : null}
      <ScrollArea
        ref={scrollAreaRef}
        orientation={scrollOrientation}
        type="always"
        className={cn("min-h-0 min-w-0 flex-1", scrollPaddingClass)}
      >
        <DayPane
          pane={pane}
          paneIndex={paneIndex}
          use12HourFormat={use12HourFormat}
          timeAxis={timeAxis}
          selectedEventId={selectedEventId}
          setSelectedEventId={setSelectedEventId}
          highlightedEventId={highlightedEventId}
          currentTimeRowStart={currentTimeRowStart}
          currentTimeRowOffsetPercent={currentTimeRowOffsetPercent}
          nowAnchorRef={nowAnchorRef}
          timeOffBlocks={timeOffBlocks}
          doctorOptions={doctorOptions}
          columnWidthMode={columnWidthMode}
          columnWidthCustomPx={columnWidthCustomPx}
          widthZoomPercent={widthZoomPercent}
          dayViewGroupBy={dayViewGroupBy}
          dragPlaceholder={dragPlaceholder}
          showSessionRail={showSessionRail}
          railWidthPx={railWidthPx}
          railColorMode={railColorMode}
          railMarginPx={railMarginPx}
          onSessionAction={onSessionAction}
          onCreateCalendarEntry={onCreateCalendarEntry}
        />
      </ScrollArea>
      {/* Active-pane ring. An overlay (not a border/ring on the wrapper) so it
          paints ABOVE the sticky all-day strip (z-40) and the columns, and never
          shifts layout. Uses ring, not border — the app's unlayered
          `* { @apply border-border }` would otherwise override border colors. */}
      {isActive ? (
        <div className="pointer-events-none absolute inset-0 z-50 ring-2 ring-inset ring-primary" />
      ) : null}
    </div>
  );
}
